import { ActivatedRouteSnapshot, Routes } from '@angular/router';
import {
  customerDetailAncestors,
  memberAccessAncestors,
} from '../utils/member-access-origin.utils';
import { accessOverviewGuard } from '../guards/access-overview.guard';
import { appReadyMatch } from '../guards/app-ready.match';
import { authGuard } from '../guards/auth.guard';
import { assessmentReviewGuard } from '../guards/assessment-review.guard';
import { complianceGuard, managerGuard } from '../guards/org-role.guard';
import { vendorAccessGuard } from '../guards/vendor-access.guard';
import { legacyVendorsRedirectGuard } from '../guards/legacy-vendors-redirect.guard';
import { profileCompletionGuard } from '../guards/profile-completion.guard';
import { profileCompletionInitGuard } from '../guards/profile-completion-init.guard';
import { billingGuard } from '../guards/billing.guard';
import { frameworksGuard } from '../guards/frameworks.guard';
import { trustCenterGuard } from '../guards/trust-center.guard';
import { unsavedChangesGuard } from '../guards/unsaved-changes.guard';
import { recoveryFlowGuard } from '../guards/recovery-flow.guard';
import { editorAccessGuard } from '../guards/editor-access.guard';
import { noWorkspaceOnlyGuard, workspaceGuard } from '../guards/workspace.guard';
import { FeatureId } from '../constants/features.constants';
import { EntityLinkService } from '../services/entity-link/entity-link';
import { controlListOpenTarget, isFrameworkControlsReturnUrl } from '../utils/return-url.utils';

/**
 * Route definitions. Every route that depends on resolved auth + initial
 * state applies `appReadyMatch` via `canMatch`, which is the single point
 * in the app that waits for boot to finish. Downstream `canActivate` guards
 * are therefore synchronous predicates over signal state.
 *
 * Exported as `withWorkspaceGuard(...)` — see that function for why
 * `workspaceGuard` is spliced in rather than written out on every route.
 */
const routeDefinitions: Routes = [
  {
    path: '',
    // Let the guarded landing route resolve auth; authenticated reloads must
    // not mount the login UI only to immediately navigate a second time.
    redirectTo: '/dashboard',
    pathMatch: 'full',
  },
  {
    path: 'login',
    canMatch: [appReadyMatch],
    loadComponent: () => import('../routes/login/login').then(m => m.Login),
  },
  {
    // Intentionally NO `notAuthedGuard`. The Cognito custom-auth recovery
    // flow signs the user in mid-flow (after confirmSignIn returns
    // isSignedIn=true), so a guard that bounces signed-in users away from
    // this route would break the recovery itself.
    //
    // `recoveryFlowGuard` activates `RecoveryFlowService` *before* the
    // component is instantiated, so by the time `app.html` evaluates,
    // the bare-outlet branch is already selected and the component
    // mounts into it directly — no destroy/remount loop. Without this
    // guard, the flag would be flipped inside the component's
    // constructor, after the template had already chosen a different
    // branch, and the freshly-mounted component would be torn down
    // again on the next change-detection pass.
    path: 'recover-mfa',
    canMatch: [appReadyMatch],
    canActivate: [recoveryFlowGuard],
    loadComponent: () => import('../routes/recover-mfa/recover-mfa').then(m => m.RecoverMfa),
  },
  {
    path: 'complete-profile',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionInitGuard],
    loadComponent: () =>
      import('../components/profile-completion/profile-completion').then(
        m => m.ProfileCompletionComponent
      ),
  },
  {
    path: 'dashboard',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: { breadcrumb: 'page_titles.dashboard' },
    loadComponent: () => import('../routes/dashboard/dashboard').then(m => m.Dashboard),
  },
  {
    // AI Import Assistant — full-screen routed surface. Renders as an
    // overlay over the app shell (shell chrome stays mounted behind it via
    // `position: fixed`), but lives at its own URL so deep-links,
    // browser back/forward, and resume sessions are all addressable.
    path: 'import',
    canMatch: [appReadyMatch],
    // Compliance capability gate: MANAGER/SUPER_MANAGER drive imports on
    // managed customer orgs; SELF_MANAGED runs imports on its own org.
    // `effectiveOrgId()` in `StateStoreService` already resolves to the
    // correct target for each role, so the assistant component needs no
    // role-specific branching.
    //
    // `viewMode: 'preserve'` — the assistant is a full-screen overlay that
    // temporarily hides the persona switcher. Whatever persona the user
    // was on when they entered is what they should see on return; without
    // this the shell would default the route to 'customer' and silently
    // flip a manager out of manager-view.
    canActivate: [authGuard, profileCompletionGuard, complianceGuard],
    data: { breadcrumb: 'page_titles.import_assistant', viewMode: 'preserve' },
    loadComponent: () =>
      import('../components/import-assistant/import-assistant').then(
        m => m.ImportAssistantComponent
      ),
  },
  {
    // Deep-link / refresh target for an in-flight import session. Same
    // component + guards as `/import`; the assistant reads `:importId`
    // from ActivatedRoute on mount and resumes that session. In-app
    // navigation between the two URLs is done via `location.replaceState`
    // (bypasses the router) so the component isn't remounted mid-wizard.
    path: 'import/:importId',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard, complianceGuard],
    data: { breadcrumb: 'page_titles.import_assistant', viewMode: 'preserve' },
    loadComponent: () =>
      import('../components/import-assistant/import-assistant').then(
        m => m.ImportAssistantComponent
      ),
  },
  {
    path: 'organisation',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: {
      breadcrumb: 'page_titles.organisation',
      chrome: 'org-settings',
      viewMode: 'preserve',
    },
    loadComponent: () => import('../routes/organisation/organisation').then(m => m.Organisation),
  },
  {
    path: 'organisation/access',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard, accessOverviewGuard],
    data: {
      breadcrumb: 'page_titles.access_overview',
      // Org-settings rail owns wayfinding — no ← Organisation crumb here.
      chrome: 'org-settings',
      viewMode: 'preserve',
    },
    loadComponent: () =>
      import('../routes/organisation/access-overview').then(m => m.AccessOverview),
  },
  // The page was called "Advanced" and sat in the rail; it now hangs off General.
  { path: 'organisation/advanced', redirectTo: '/organisation/preferences', pathMatch: 'full' },
  {
    // Nested under General rather than a rail entry of its own: it holds one
    // organisation-wide setting, and General links to it.
    path: 'organisation/preferences',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: {
      breadcrumb: 'page_titles.organization_preferences',
      chrome: 'org-settings',
      viewMode: 'preserve',
      breadcrumbAncestors: () => [{ label: 'page_titles.organisation', url: '/organisation' }],
    },
    loadComponent: () =>
      import('../routes/organisation/organization-ai-settings').then(m => m.OrganizationAiSettings),
  },
  {
    path: 'organisation/members',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: {
      breadcrumb: 'page_titles.members',
      // Org-settings rail owns wayfinding — no ← Organisation crumb here.
      chrome: 'org-settings',
      viewMode: 'preserve',
    },
    loadComponent: () =>
      import('../routes/organisation/organisation-members').then(m => m.OrganisationMembers),
  },
  {
    // Per-member access configuration — grants and capability narrowing.
    // Nested under the members list because that is the only way in: the
    // grantee is chosen by navigating to their row, deliberately not from a
    // user picker (the "list all users" handler returns the entire pool).
    path: 'organisation/members/:userId/access',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    canDeactivate: [unsavedChangesGuard],
    data: {
      breadcrumb: 'page_titles.member_access',
      // Org-settings chrome for home-org access; suppressed when `returnTo` is a
      // customer Members tab (see resolveActiveRouteChrome) so the rail does
      // not offer home-org Organisation links while editing a customer member.
      chrome: 'org-settings',
      // Bimodal, so the persona must not flip on arrival. Untagged routes
      // default to 'customer', which threw a manager out of manager-view
      // mid-task — they came from `/customers/:id/members` and crumb
      // climb returns them there, to a route that is manager-only chrome.
      viewMode: 'preserve',
      // Reached from four places (Members, Access overview, a customer's Team
      // access or Members tab); "back" returns to the one you left, with its
      // filters. Derived from `returnTo` rather than from the referrer or
      // navigation state, both of which are wiped a tick after arrival by the
      // sticky-param healing pass in `StateStoreService`; the query param
      // survives that, refresh and deep links. See `memberAccessAncestors`.
      breadcrumbAncestors: (snapshot: ActivatedRouteSnapshot) =>
        memberAccessAncestors(snapshot.queryParamMap.get('returnTo')),
    },
    loadComponent: () =>
      import('../routes/organisation/member-access/member-access').then(
        m => m.MemberAccessComponent
      ),
  },
  {
    path: 'organisation/support',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: {
      breadcrumb: 'page_titles.support',
      // Org-settings rail owns wayfinding (dropdown Support is a shortcut here).
      chrome: 'org-settings',
      viewMode: 'preserve',
    },
    loadComponent: () =>
      import('../routes/organisation-support/organisation-support').then(
        m => m.OrganisationSupport
      ),
  },
  {
    path: 'assessment',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: { breadcrumb: 'page_titles.assessment' },
    loadComponent: () => import('../routes/assessment/assessment').then(m => m.Assessment),
  },
  {
    path: 'assessments-for-review',
    canMatch: [appReadyMatch],
    // `assessmentReviewGuard` on top of `managerGuard`: a delegate passes the
    // org-type check by virtue of signing into the platform org, so the
    // capability is what actually decides this queue.
    canActivate: [authGuard, profileCompletionGuard, managerGuard, assessmentReviewGuard],
    data: { breadcrumb: 'page_titles.assessments_for_review', viewMode: 'manager' },
    loadComponent: () =>
      import('../routes/assessments-for-review/assessments-for-review').then(
        m => m.AssessmentsForReview
      ),
  },
  {
    path: 'documents',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: { breadcrumb: 'page_titles.documents' },
    loadComponent: () => import('../routes/documents/documents').then(m => m.Documents),
  },
  {
    // Context Documents — supporting reference-material library used by
    // document generation. Standalone route + sidebar item; previously
    // lived as a tab inside `/documents`.
    path: 'context-documents',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: { breadcrumb: 'page_titles.context_documents' },
    loadComponent: () =>
      import('../routes/context-documents/context-documents').then(m => m.ContextDocuments),
  },
  {
    path: 'frameworks',
    canMatch: [appReadyMatch],
    // `framework_read` on the effective org, not org type — a STANDARD org's
    // own admin authors their frameworks, and the BE has permitted that since
    // VER-326. Entitlement and write capability are the page's job.
    canActivate: [authGuard, profileCompletionGuard, frameworksGuard],
    data: { breadcrumb: 'page_titles.frameworks' },
    loadComponent: () => import('../routes/frameworks/frameworks').then(m => m.Frameworks),
  },
  {
    // Framework detail shell — identity card + tabs (overview, controls,
    // history, tasks). Each tab is a child route so URLs deep-link.
    path: 'frameworks/:frameworkId',
    canMatch: [appReadyMatch],
    // Same gate as the list. This was `managerGuard`, which is stricter still
    // — it excludes SELF_MANAGED — so opening a framework from a list that
    // rendered it bounced the caller to /assessment.
    canActivate: [authGuard, profileCompletionGuard, frameworksGuard],
    data: {
      breadcrumb: 'page_titles.framework_detail',
      breadcrumbAncestors: [{ label: 'page_titles.frameworks', url: '/frameworks' }],
    },
    loadChildren: () =>
      import('../routes/frameworks/framework-detail/framework-detail.routes').then(m => m.routes),
  },
  {
    // Tasks is bimodal: in customer-view it shows the picked customer's
    // tasks; in manager-view it shows the manager's own org's tasks.
    // `viewMode: 'preserve'` keeps whichever persona the user clicked
    // from; the Tasks component reads `stateService.viewMode()` to
    // decide which orgId to fetch against.
    path: 'tasks',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: { breadcrumb: 'page_titles.tasks', viewMode: 'preserve' },
    loadComponent: () => import('../routes/tasks/tasks').then(m => m.Tasks),
  },
  {
    path: 'customers',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard, managerGuard],
    data: { breadcrumb: 'page_titles.customers', viewMode: 'manager' },
    loadComponent: () => import('../routes/customers/customers').then(m => m.Customers),
  },
  {
    // Customer-detail shell — identity card + tab nav. Each tab is a
    // child route (overview, subscription). Breadcrumb trail: Customers
    // → Customer details. The h1 inside the shell is the customer name;
    // we don't duplicate it in the trail per the project's breadcrumb
    // convention (BreadcrumbService doc).
    path: 'customers/:id',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard, managerGuard],
    data: {
      breadcrumb: 'page_titles.customer_detail',
      // Customers is home; a page opened from the Access overview goes back there.
      breadcrumbAncestors: (snapshot: ActivatedRouteSnapshot) =>
        customerDetailAncestors(snapshot.queryParamMap.get('returnTo')),
      viewMode: 'manager',
    },
    loadComponent: () => import('../routes/customers/customer-detail').then(m => m.CustomerDetail),
    loadChildren: () =>
      import('../routes/customers/customer-detail.routes').then(m => m.CUSTOMER_DETAIL_ROUTES),
  },
  {
    path: 'settings/billing',
    canMatch: [appReadyMatch],
    // `billing_read`, not the ADMIN role — the sidebar entry has asked the
    // capability question since the Billing area became grantable, and the
    // route asking a different one bounced every member who held it.
    canActivate: [authGuard, profileCompletionGuard, billingGuard],
    data: {
      breadcrumb: 'page_titles.billing',
      // Org-scoped surface under Organisation settings chrome.
      chrome: 'org-settings',
      viewMode: 'preserve',
    },
    loadComponent: () => import('../routes/billing/billing').then(m => m.Billing),
  },
  {
    path: 'billing',
    redirectTo: 'settings/billing',
    pathMatch: 'full',
  },
  {
    // `/vendors` is a virtual hub — it always redirects to a real
    // sub-page (`inventory` / `inbox` / `registry` / `activities` /
    // `lists`) and never renders standalone. Deliberately omits
    // `data.breadcrumb` so it doesn't appear in the trail or wire a
    // dead-end "Back to Vendors" affordance into the page header —
    // "Vendors" is a sidebar grouping label, not a destination. Sub-
    // routes that want Inventory / Inbox as their ancestor declare it
    // explicitly via `breadcrumbAncestors`.
    path: 'vendors',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard, vendorAccessGuard, legacyVendorsRedirectGuard],
    loadChildren: () => import('../routes/vendors/vendors.routes').then(m => m.routes),
  },
  {
    // Standalone editor — opens in a new tab from the parent list page.
    // Intentionally omits `data.breadcrumb` so it doesn't participate in
    // the breadcrumb trail; the editor's own inline document-name editor
    // is the page identity.
    path: 'edit-document',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard, editorAccessGuard],
    canDeactivate: [unsavedChangesGuard],
    // Bimodal: a customer editing their own doc is in customer view; a
    // manager editing a customer's doc is in manager view. The route
    // itself doesn't dictate persona, so preserve whichever the user
    // arrived in. Without this, `syncViewModeFromRoute` fires the
    // default 'customer' on every same-route nav (rail row clicks emit
    // query-param-only navigations), which would clobber the picked
    // manager view + orgId on every doc switch.
    data: { viewMode: 'preserve', featureIds: [FeatureId.DOCUMENTS] },
    loadComponent: () => import('../routes/edit-document/edit-document').then(m => m.EditDocument),
  },
  {
    // Standalone editor — see comment above on `edit-document`.
    path: 'edit-assessment',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard, editorAccessGuard],
    canDeactivate: [unsavedChangesGuard],
    // Assessment type is unknown at guard time; admit if the caller can
    // read ANY assessment feature. Per-type gating runs in the component.
    data: {
      viewMode: 'preserve',
      featureIds: [FeatureId.AI_RISK_ASSESSMENT, FeatureId.DPIA, FeatureId.TIA],
    },
    loadComponent: () =>
      import('../routes/edit-assessment/edit-assessment').then(m => m.EditAssessment),
  },
  {
    path: 'controls',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: { breadcrumb: 'page_titles.controls' },
    loadComponent: () => import('../routes/controls/controls').then(m => m.Controls),
  },
  {
    // Control hub — identity + work cards + details; Period / Schedule /
    // Activity are section children. `/controls?open=<id>` opens the list
    // preview drawer (return-from-hub / deep-link); full-page hub is
    // `/controls/:id` (optional `?returnTo=` for multi-origin back).
    path: 'controls/:controlId',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: {
      breadcrumb: 'page_titles.control_detail',
      // Same `returnTo` gate as EntityLinkService — crumb climb restores
      // the originating list with `?open=<id>` (drawer reselect). Framework
      // controls origins keep a Frameworks → controls-section trail.
      breadcrumbAncestors: (snapshot: ActivatedRouteSnapshot) => {
        const raw = snapshot.queryParamMap.get(EntityLinkService.RETURN_TO_PARAM);
        const controlId = snapshot.paramMap.get('controlId');
        if (!controlId) {
          return [{ label: 'page_titles.controls', url: '/controls' }];
        }
        const { path, queryParams } = controlListOpenTarget(raw, controlId);
        const listSegments = path.split('/').filter(Boolean).map(decodeURIComponent);

        if (isFrameworkControlsReturnUrl(raw)) {
          return [
            { label: 'page_titles.frameworks', url: '/frameworks' },
            {
              label: 'framework_detail.section_controls',
              url: ['/', ...listSegments],
              queryParams,
            },
          ];
        }

        return [
          {
            label: 'page_titles.controls',
            url: ['/', ...listSegments],
            queryParams,
          },
        ];
      },
    },
    loadChildren: () =>
      import('../routes/controls/control-detail/control-detail.routes').then(m => m.routes),
  },
  {
    path: 'incidents',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: { breadcrumb: 'page_titles.incidents' },
    loadComponent: () => import('../routes/incidents/incidents').then(m => m.Incidents),
  },
  {
    path: 'incidents/:incidentId',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: {
      breadcrumb: 'page_titles.incident_detail',
      breadcrumbAncestors: [{ label: 'page_titles.incidents', url: '/incidents' }],
    },
    loadComponent: () =>
      import('../routes/incidents/incident-detail/incident-detail').then(m => m.IncidentDetailPage),
  },
  {
    // Trust Center is a customer-org feature — the customer (or a
    // manager acting on a picked customer in customer-view) configures
    // the public trust-page widget for that org. Data is scoped by
    // `effectiveOrgId` so it follows the sidebar picker selection.
    // `trustCenterGuard` allows Trust Center / Full Suite subscribers
    // and manager-class users in customer-view persona.
    path: 'trust-center',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard, trustCenterGuard],
    data: { breadcrumb: 'page_titles.trust_center' },
    loadComponent: () => import('../routes/trust-center/trust-center').then(m => m.TrustCenter),
  },
  {
    path: 'settings',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, profileCompletionGuard],
    data: {
      breadcrumb: 'page_titles.settings',
      chrome: 'user-settings',
      viewMode: 'preserve',
    },
    loadComponent: () => import('../routes/settings/settings').then(m => m.Settings),
  },
  // Retired surfaces — both the standalone /subscriptions page and the
  // separate /admin shell now live as a Subscription section inside the
  // per-customer page at /customers/:id. Sending bookmarks back to the
  // customer list lets the manager re-pick the customer they were
  // configuring.
  { path: 'subscriptions', redirectTo: '/customers', pathMatch: 'full' },
  { path: 'admin/subscriptions', redirectTo: '/customers', pathMatch: 'full' },
  { path: 'admin', redirectTo: '/customers', pathMatch: 'full' },
  {
    // The zero-grant / off-boarding screen. Deliberately carries neither
    // `profileCompletionGuard` (so `withWorkspaceGuard` leaves it alone —
    // guarding it with the very guard that redirects here would loop) nor
    // any role guard: a delegate with nothing granted must be able to reach
    // the explanation whatever else is true of their account.
    path: 'no-workspace',
    canMatch: [appReadyMatch],
    canActivate: [authGuard, noWorkspaceOnlyGuard],
    data: { breadcrumb: 'page_titles.no_workspace' },
    loadComponent: () => import('../routes/no-workspace/no-workspace').then(m => m.NoWorkspace),
  },
  {
    // Critical boot-error page — reached when a pre-load failure (whoami HTTP
    // error, network down) prevents the app from determining account state.
    // Deliberately carries no appReadyMatch, no authGuard, and is excluded from
    // the workspaceGuard splice below: the error page must be reachable even
    // when the boot sequence itself is the thing that failed.
    path: 'error',
    loadComponent: () =>
      import('../routes/critical-error/critical-error').then(m => m.CriticalError),
  },
  {
    path: '**',
    redirectTo: '/login',
  },
];

/**
 * Splice `workspaceGuard` into every authenticated route, immediately after
 * `profileCompletionGuard`.
 *
 * Done here rather than written out on ~30 route objects for two reasons.
 * It keeps the ordering invariant in **one** place — after profile
 * completion, before the role guards, for the reasons documented on the
 * guard itself. And it makes the safe behaviour the default: a route added
 * later inherits the lockout screen without anyone remembering to ask for
 * it, which is the right default for a guard whose absence means a
 * workspace-less user reaches a broken page.
 *
 * Self-selecting: presence of `profileCompletionGuard` is what marks a route
 * as authenticated app surface. `/login`, `/recover-mfa`, `/no-workspace`
 * and the bare redirects carry none, so they are returned untouched.
 */
function withWorkspaceGuard(definitions: Routes): Routes {
  return definitions.map(route => {
    const guards = route.canActivate;
    if (!guards) return route;
    const anchor = guards.indexOf(profileCompletionGuard);
    if (anchor === -1) return route;
    return {
      ...route,
      canActivate: [...guards.slice(0, anchor + 1), workspaceGuard, ...guards.slice(anchor + 1)],
    };
  });
}

export const routes: Routes = withWorkspaceGuard(routeDefinitions);
